import * as Sentry from "@sentry/vue";
import { createApp } from "vue";
import App from "./App.vue";
import { router } from "./router";
// Load the Leaflet

import "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/leaflet.markercluster.js";
import "leaflet/dist/leaflet.css";

// Global libraries
import axios from "axios"; // XMLHttpRequests helper
import VueAxios from "vue-axios"; // XMLHttpRequests helper

// 3rd party libraries
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import I18NextVue from "i18next-vue";
import { fontawesome } from "./fontawesome"; // Fontawesome Web Iconfont
import { AvailableLanguages } from "./types";

// tailwind & styles
import "@/assets/css-snippets/AlarmBellSwing.css";
import { createPinia } from "pinia";
import "./tailwind.css";
import { useGlobalConfigsStore } from "./storeModules/globalConfigs/globalConfigs";

const app = createApp(App);
// pinia
const pinia = createPinia();

Sentry.init({
  app,
  environment: globalThis.location.hostname,
  dsn: "https://870c1dfac3fdf54812200af788edd59b@o4508342566518784.ingest.de.sentry.io/4508342570713168",
  integrations: [
    Sentry.browserTracingIntegration({
      router,
    }),
    Sentry.replayIntegration(),
    // TODO: Clear this feature with the product team.
    // Sentry.feedbackIntegration({
    //   attach: true,
    //   colorScheme: "system",
    // }),
  ],
  tunnel: "/sentry",
  // Tracing
  tracesSampleRate: 1, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  profilesSampleRate: 1,
});
app.use(pinia);
app.use(I18NextVue, {
  i18next,
  rerenderOn: ["languageChanged", "loaded"],
});

const globalConfigStore = useGlobalConfigsStore();

app.use(router);
app.use(VueAxios, axios);
app.component("font-awesome-icon", fontawesome);
app.mount("#app");
i18next
  .use(LanguageDetector)
  .use(HttpApi)
  .init({
    fallbackLng: "en",
    ns: ["translation"],
    defaultNS: "translation",
    fallbackNS: "translation",
    debug: import.meta.env.DEV,
    saveMissing: false,
    backend: {
      loadPath: "/api/i18n/translations?lang={{lng}}",
    },
  });

const fetchAvailableLanguages = async () => {
  try {
    const response = await axios.post("/api/i18n/languages");
    const languages = response.data;
    if (import.meta.env.MODE === "development") {
      // Save all the available languages for development purposes.
      globalConfigStore.$patch({
        availableLanguages: languages,
      });
    } else {
      // Manually filter out the languages that are not yet supported
      // The threshold is for new features. Sometimes a new feature has missing translations in prod. No need to block the entire language for that.
      const filteredLanguages: AvailableLanguages = {};

      Object.keys(languages)
        .filter((languageCode) => languages[languageCode].translated.production > 0.9)
        .filter((languageCode) => languageCode != "he") // Turn off Hebrew in PROD while we're working on the translations
        .forEach((languageCode) => {
          filteredLanguages[languageCode] = languages[languageCode];
        });

      globalConfigStore.$patch({
        availableLanguages: filteredLanguages,
      });
    }
  } catch (error) {
    console.error("Failed to fetch available languages:", error);
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
fetchAvailableLanguages();
//loadCookieBannerScript();
